/* The Overview's line: how many questions arrived, day by day. It belongs to that panel
 * alone, like `StatTiles`, and is the one thing on it that a row of totals cannot say —
 * whether 337 questions were a steady month or one afternoon somebody demonstrated it.
 *
 * It carries its own date range, which the rest of the page does not follow: the toolbar's
 * period is what every figure on the site is computed over, and letting one card quietly
 * redefine it would leave the tiles above describing a different fortnight. So the heading
 * always spells out the days being drawn.
 */

import { useMemo, useState } from 'react';
import {
  Button,
  DateRangePicker,
  SkeletonText,
  makeRecentDateRangePresets,
  toDateString,
  type DateRange,
} from '@nextleap-al/admin-ui';

import type { Health } from '../api/types';
import { series } from '../charts/chart';
import { LineChart } from '../charts/charts';
import { PanelCard, PanelError } from './Panel';
import { useApi } from '../hooks/useApi';
import { plural, shortDate } from '../lib/format';
import { useApp } from '../state/app';

const DAY_MS = 86_400_000;

/** Beyond this many points the line is a smear, so the days are summed into equal buckets. */
const MAX_POINTS = 60;

interface Point {
  /** `YYYY-MM-DD` — the first day the point counts. */
  day: string;
  turns: number;
}

/**
 * The daily counts between two days, with the days nothing was asked put back in as zero:
 * the query returns only the days that have rows, and a line drawn straight from Friday to
 * Monday claims a weekend of traffic that never happened.
 *
 * A long window is then summed into equal buckets rather than plotted a day at a time, so a
 * year is a readable line instead of three hundred points.
 */
function volume(daily: Health['daily'], from: string, to: string): { points: Point[]; bucket: number } {
  const at = (iso: string) => new Date(`${iso}T00:00:00Z`).getTime();
  const start = at(from);
  const end = at(to);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return { points: [], bucket: 1 };

  const counted = new Map(daily.map((d) => [d.day, d.turns]));
  const days: Point[] = [];
  for (let t = start; t <= end; t += DAY_MS) {
    const day = new Date(t).toISOString().slice(0, 10);
    days.push({ day, turns: counted.get(day) ?? 0 });
  }

  const bucket = Math.ceil(days.length / MAX_POINTS);
  if (bucket <= 1) return { points: days, bucket: 1 };

  const points: Point[] = [];
  for (let i = 0; i < days.length; i += bucket) {
    const chunk = days.slice(i, i + bucket);
    points.push({ day: chunk[0].day, turns: chunk.reduce((sum, d) => sum + d.turns, 0) });
  }
  return { points, bucket };
}

/** Whole days back from now, which is what the API's `days` parameter counts. */
const daysBack = (from: Date): number =>
  Math.max(1, Math.ceil((Date.now() - from.getTime()) / DAY_MS) + 1);

/* `YYYY-MM-DD` sorts as it reads, so the two ends of a range need no dates parsed. */
const earlier = (a: string, b: string): string => (a < b ? a : b);
const later = (a: string, b: string): string => (a > b ? a : b);

export function VolumeChart({ daily }: { daily: Health['daily'] }) {
  const { days, system } = useApp();
  const [range, setRange] = useState<DateRange | undefined>();

  // Built once per mount rather than at module scope: "Today" computed when the bundle was
  // parsed is the wrong day on a page left open overnight.
  const presets = useMemo(() => makeRecentDateRangePresets(), []);
  const today = toDateString(new Date());

  // A range reaching back further than the page's own window is fetched again, since that
  // window is the only thing the API can be asked in. A range inside it re-uses what the
  // panel has already loaded, so picking dates costs nothing until it has to.
  const picked = range?.from;
  const wider = picked ? daysBack(picked) > days : false;
  const query = useApi<Health>(
    '/api/health-metrics',
    { days: picked ? daysBack(picked) : days, system },
    wider,
  );

  const source = wider ? query.data?.daily : daily;

  // The picker offers "This month", which ends in the future; drawing to the end of it would
  // put a fortnight of zeroes on the line, reading as a fortnight of silence.
  const from = picked ? toDateString(picked) : (daily[0]?.day ?? today);
  const to = picked
    ? earlier(toDateString(range?.to ?? picked), today)
    : later(daily[daily.length - 1]?.day ?? today, today);

  const { points, bucket } = volume(source ?? [], from, to);

  const heading = bucket === 1 ? 'Questions per day' : `Questions per ${bucket} days`;
  const span = `${shortDate(from)} to ${shortDate(to)}`;
  const asked = points.reduce((sum, p) => sum + p.turns, 0);

  const control = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <DateRangePicker
        value={range}
        onChange={setRange}
        presets={presets}
        toDate={new Date()}
        align="end"
        placeholder="Any dates"
        aria-label="Dates to draw"
        className="w-[200px] sm:w-[230px]"
      />
      {picked && (
        <Button variant="ghost" size="sm" onClick={() => setRange(undefined)}>
          Back to the period
        </Button>
      )}
    </div>
  );

  return (
    <PanelCard
      title={heading}
      hint={
        // The dates are said out loud either way. This card is the one thing on the page not
        // governed by the toolbar, and a line whose window is a mystery is worse than none.
        picked
          ? `${plural(asked, 'question')} between ${span} — these dates only, not the period the numbers above are counted over.`
          : `${span}, including the days nobody asked anything.`
      }
      action={control}
    >
      {query.error ? (
        <PanelError error={query.error} />
      ) : wider && !query.data ? (
        <SkeletonText lines={4} />
      ) : points.length < 2 ? (
        <p className="py-10 text-center text-sm text-[var(--text-muted)]">
          {points.length === 1
            ? 'One day is a dot, not a line — pick a wider range.'
            : 'Nothing to draw for these dates.'}
        </p>
      ) : (
        <LineChart
          label={`Questions asked, ${span}`}
          labels={points.map((p) => shortDate(p.day))}
          series={[{ name: 'Questions', color: series()[0], values: points.map((p) => p.turns) }]}
        />
      )}
    </PanelCard>
  );
}
